import { useState, useEffect, useCallback, useRef } from 'react';
import { API_URL } from '../utils/api';

const STORAGE_KEY = 'olso_app_version';
const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

interface UseVersionCheckResult {
  updateAvailable: boolean;
  dismiss: () => void;
  update: () => void;
}

export function useVersionCheck(): UseVersionCheckResult {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const dismissedRef = useRef(false);

  const checkVersion = useCallback(async () => {
    if (dismissedRef.current) return;
    try {
      const res = await fetch(`${API_URL}/api/version`, { cache: 'no-store' });
      if (!res.ok) return;
      const data: { version: string } = await res.json();
      const latest = data.version;
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && stored !== latest) {
        setUpdateAvailable(true);
      }
      // Always persist the latest known version
      localStorage.setItem(STORAGE_KEY, latest);
    } catch {
      // Silently ignore — no network, background tab, etc.
    }
  }, []);

  useEffect(() => {
    checkVersion();
    const id = setInterval(checkVersion, CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [checkVersion]);

  const dismiss = useCallback(() => {
    dismissedRef.current = true;
    setUpdateAvailable(false);
  }, []);

  const update = useCallback(() => {
    window.location.reload();
  }, []);

  return { updateAvailable, dismiss, update };
}
